"use client";

import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatIsAnArcFlashLabel() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const origins = [{
    coords: "NFPA 70E",
    name: "COMPLIANCE"
  }, {
    coords: "ANSI Z535",
    name: "STANDARDS"
  }, {
    coords: "OSHA 1910",
    name: "REGULATION"
  }];
  const stats = [];
  return <section data-section-id="1302" id="what-is-an-arc-flash-label" className="bg-background text-foreground relative overflow-hidden border-b border-border">
      {}
      <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none" style={{
      backgroundImage: "repeating-linear-gradient(to right, var(--secondary) 0 4px, transparent 4px 10px)",
      backgroundSize: "10px 2px",
      backgroundPosition: "0 center",
      backgroundRepeat: "repeat-x"
    }} aria-hidden="true" />

      <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 py-28 md:py-40">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute select-none font-serif text-[240px] md:text-[360px] leading-none opacity-[0.08] top-12 md:top-16 left-2 md:left-6" style={{
        WebkitTextStroke: "1.5px var(--foreground)",
        color: "transparent"
      }}>
          01
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={titleRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            {}
            <div className="font-mono uppercase text-xs md:text-sm tracking-[0.18em] text-foreground/90 mb-4">
              § SAFETY — ARC FLASH COMPLIANCE
            </div>

            {}
            <div className="flex items-center gap-2 mb-10">
              <Separator className="bg-foreground/60 flex-1 h-px" />
              <span className="w-2 h-2 bg-primary rounded-lg" aria-hidden="true" />
            </div>

            {}
            <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-tight" style={{
            fontSize: "clamp(2rem, 4.4vw, 4rem)",
            letterSpacing: "-0.015em"
          }}>
              Defining{" "}
              <span className="relative inline-block italic">
                <span className="relative z-10">critical</span>
                <span className={`absolute left-0 bottom-0.5 h-[2px] bg-primary transition-[width] duration-700 ${titleVisible ? "w-full" : "w-0"}`} style={{
                transitionDelay: "200ms",
                transitionTimingFunction: "cubic-bezier(0.65, 0, 0.35, 1)"
              }} aria-hidden="true" />
              </span>{" "}
              safety standards.
            </h2>

            {}
            <ul className="mt-12 space-y-4">
              {origins.map((origin, i) => <li data-index={i} key={origin.name} className="flex items-center gap-3 font-mono text-xs md:text-sm tracking-[0.1em] text-foreground/85">
                  <span className="w-2 h-2 bg-primary rounded-lg shrink-0" aria-hidden="true" />
                  <span className="text-foreground">{origin.coords}</span>
                  <span className="text-foreground/60">·</span>
                  <span className="text-foreground">{origin.name}</span>
                </li>)}
            </ul>
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-7 relative transition-all duration-700 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDelay: "120ms"
        }}>
            {}
            <div className="font-default text-foreground/90 leading-[1.65]" style={{
            columnCount: 1,
            columnGap: "48px",
            fontSize: "clamp(1rem, 1.1vw, 1.0625rem)"
          }}>
              <div className="md:[column-count:2] md:[column-gap:48px]">
                <p className="mb-5">
                  <span className="float-left font-default font-bold text-primary mr-2 leading-[0.85] pt-1 relative" style={{
                  fontSize: "5em"
                }}>
                    A
                    <span className="absolute left-0 right-2 bottom-1 h-[2px] bg-primary" aria-hidden="true" />
                  </span>
                  n arc flash label is a vital safety component required by NFPA 70E to warn personnel of potential electrical hazards. These labels provide essential data, including incident energy, arc flash boundaries, and required personal protective equipment (PPE), ensuring that workers can operate safely within industrial environments.
                </p>
                <p className="mb-5">
                  Our manufacturing process utilizes high-durability materials designed to withstand harsh industrial conditions. Whether you require phenolic, metal, or adhesive-backed labels, we ensure every piece of information is engraved with precision to maintain legibility and compliance over the long term.
                </p>
                <p>
                  We specialize in custom submittal packages and rapid production, helping facility managers and electrical contractors meet strict safety deadlines. Our commitment to quality ensures that your facility remains compliant with the latest OSHA and ANSI standards.
                </p>
              </div>
            </div>

            {}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {stats.map((stat, i) => <div data-index={i} key={stat.label} className="bg-card text-card-foreground border border-border p-6 rounded-lg shadow-md transition-colors duration-300">
                  <div className="font-mono uppercase text-[0.7rem] tracking-[0.18em] text-card-foreground/70 mb-3">
                    {stat.label}
                  </div>
                  <div className="font-default font-bold leading-none text-card-foreground" style={{
                fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)",
                letterSpacing: "-0.015em"
              }}>
                    {stat.value}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Separator className="bg-card-foreground/40 flex-1 h-px" />
                    <span className="w-1.5 h-1.5 bg-primary rounded-lg" aria-hidden="true" />
                  </div>
                </div>)}
            </div>

            {}
            
          </div>
        </div>
      </div>
    </section>;
}